import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  View,
  Pressable,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from "react-native";
import { useAuthGate } from "./AuthGateContext";
import { EMBER } from "../onboarding/theme";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import type { BottomTabBarProps } from "@react-navigation/bottom-tabs";
import { StackActions, useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  House,
  HeartPulse,
  Wallet,
  Compass,
  BookOpen,
} from "lucide-react-native";
import Animated, {
  Easing,
  interpolate,
  runOnJS,
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { haptic, springs } from "@louise/moon-ui";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { GradientOrb } from "@src/components/ui/GradientOrb";
import type { MainTabParamList } from "./types";
import PlanHomeScreen from "./screens/PlanHomeScreen";
import CareNavigator from "./screens/CareScreen";
import CostsNavigator from "./screens/CostsScreen";
import AccessNavigator from "./screens/AccessScreen";
import ChatNavigator from "./screens/ChatScreen";
import ChatHubScreen from "./screens/chat/ChatHubScreen";
import OAuthPhoneLinkPrompt from "./components/OAuthPhoneLinkPrompt";
import { ErrorBoundary } from "../error/ErrorBoundary";
import { TabBarOverrideProvider } from "./TabBarOverrideContext";
import {
  DocumentUploadControllerProvider,
  useDocumentUploadController,
} from "./DocumentUploadController";
import { DocumentProcessingActivityControl } from "@src/packages/document-vault/DocumentProcessingActivity";
import type { RootStackParamList } from "../App";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const Tab = createBottomTabNavigator<MainTabParamList>();

const TAB_ICONS: Record<
  Exclude<keyof MainTabParamList, "Chat">,
  typeof House
> = {
  Home: House,
  Care: HeartPulse,
  Costs: Wallet,
  Access: Compass,
  Learn: BookOpen,
};

function TabButton({
  icon: Icon,
  isFocused,
  onPress,
  onLongPress,
  accessibilityLabel,
  action,
  floating = false,
  children,
}: {
  icon?: typeof House;
  isFocused: boolean;
  onPress: () => void;
  onLongPress: () => void;
  accessibilityLabel?: string;
  /** Stable analytics identifier, since the visible tab label is translated. */
  action?: string;
  floating?: boolean;
  children?: ReactNode;
}) {
  const capturePress = usePressCapture("TabButton");
  const hasCustomIcon = children != null;
  const reducedMotion = useReducedMotion();
  const scale = useSharedValue(1);
  const hovered = useSharedValue(0);
  const pressed = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    backgroundColor: isFocused ? "rgba(255, 255, 255, 0.15)" : "transparent",
  }));
  const iconOpacity = useAnimatedStyle(() => {
    const resting = hasCustomIcon
      ? 1
      : isFocused
        ? 1
        : 0.45 + hovered.value * 0.55;
    return { opacity: resting * (1 - pressed.value * 0.3) };
  });

  // Without the press spring the wrapper has nothing left to animate, and an
  // Animated.View would still write a `scale(1)` transform. That identity
  // transform is enough to keep the whole bar on its own GPU layer, where the
  // icon caps re-rasterize with a different antialiasing result per paint.
  const IconWrap = reducedMotion ? View : Animated.View;
  const wrapStyle = reducedMotion
    ? {
        backgroundColor: isFocused
          ? "rgba(255, 255, 255, 0.15)"
          : "transparent",
      }
    : animatedStyle;

  const hoverHandlers =
    Platform.OS === "web"
      ? {
          onHoverIn: () => {
            hovered.value = withTiming(1, { duration: 150 });
          },
          onHoverOut: () => {
            hovered.value = withTiming(0, { duration: 150 });
          },
        }
      : {};

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(accessibilityLabel), action);
        onPress();
      }}
      onLongPress={onLongPress}
      onPressIn={() => {
        // The preference removes the motion, not the feedback: the shrink is
        // replaced by the same dim the shared IconButton uses when pressed.
        if (reducedMotion) {
          pressed.value = 1;
          return;
        }
        scale.value = withSpring(0.85, springs.press);
      }}
      onPressOut={() => {
        if (reducedMotion) {
          pressed.value = 0;
          return;
        }
        scale.value = withSpring(1, springs.press);
      }}
      {...hoverHandlers}
      accessibilityRole="button"
      accessibilityState={isFocused ? { selected: true } : {}}
      accessibilityLabel={accessibilityLabel}
      style={floating ? styles.floatingTabButton : styles.tabButton}
    >
      <IconWrap
        style={[
          hasCustomIcon ? styles.customIconWrap : styles.iconWrap,
          wrapStyle,
        ]}
      >
        <Animated.View style={iconOpacity}>
          {children ??
            (Icon ? (
              <Icon
                size={20}
                color={isFocused ? "#FFFFFF" : "#FFFFFF"}
                strokeWidth={isFocused ? 2 : 1.5}
              />
            ) : null)}
        </Animated.View>
      </IconWrap>
    </Pressable>
  );
}

function FloatingTabBar({
  onOpenChat,
  ...props
}: BottomTabBarProps & { onOpenChat: () => void }) {
  const {
    processingItems,
    updateProcessingItem,
    dismissProcessingItem,
    pickAndOpenDocumentUpload,
  } = useDocumentUploadController();
  const { state, descriptors, navigation } = props;
  const insets = useSafeAreaInsets();
  const bottomPadding = Math.max(insets.bottom, 12);

  // Hide tab bar when inside a medical history flow
  const careRoute = state.routes.find((r) => r.name === "Care");
  const careState = careRoute?.state;
  const currentCareRoute = careState?.routes[careState.index ?? 0];
  if (currentCareRoute?.name?.endsWith("Flow")) return null;

  return (
    <View
      style={[styles.tabBarOuter, { paddingBottom: bottomPadding }]}
      pointerEvents="box-none"
    >
      <View style={styles.tabBarCluster}>
        <View style={styles.tabBarPill}>
          {state.routes.map((route, index) => {
            if (route.name === "Chat") return null;
            const { options } = descriptors[route.key]!;
            const isFocused = state.index === index;
            const Icon = TAB_ICONS[route.name as keyof typeof TAB_ICONS];
            // Tabs render icon-only with no tabBarAccessibilityLabel set, so
            // fall back to the visible label / route name to give screen
            // readers a name for each tab.
            const tabLabel =
              typeof options.tabBarLabel === "string"
                ? options.tabBarLabel
                : route.name;

            return (
              <TabButton
                key={route.key}
                icon={Icon}
                isFocused={isFocused}
                accessibilityLabel={
                  options.tabBarAccessibilityLabel ?? `${tabLabel} tab`
                }
                action={`tab_${route.name.toLowerCase()}`}
                onPress={() => {
                  haptic("light");
                  const event = navigation.emit({
                    type: "tabPress",
                    target: route.key,
                    canPreventDefault: true,
                  });
                  if (!event.defaultPrevented) {
                    if (isFocused) {
                      // Tapping the tab you are already on returns that tab to
                      // its own first screen.
                      //
                      // This used to dispatch an untargeted `CommonActions.reset`
                      // carrying `[{ name: route.name }]`. Every tab name here
                      // also exists on the root stack — `Home` most of all — so
                      // the action could be handled one level up instead, which
                      // remounts this navigator. On an unauthenticated visit that
                      // remount re-runs the auth-gate redirect below and throws
                      // the patient into onboarding, so pressing Home while on
                      // Home left onboarding on screen. The reset was also
                      // destructive in its own right: a single-route payload
                      // discards the sibling tabs' saved state.
                      //
                      // Targeting the tab's nested navigator by key keeps the
                      // action inside that tab and cannot reach a parent. Tabs
                      // whose content is a plain screen rather than a navigator
                      // (Home) have no nested state and nothing to pop.
                      const nestedNavigatorKey = route.state?.key;
                      if (nestedNavigatorKey) {
                        navigation.dispatch({
                          ...StackActions.popToTop(),
                          target: nestedNavigatorKey,
                        });
                      }
                    } else {
                      // Switching to a tab whose top-of-stack was entered via
                      // cross-tab navigation (has `returnTab` in its params)
                      // pops that stack to root first. Otherwise the icon tap
                      // reopens the cross-tab detail, whose stale `returnTab`
                      // then bounces the user back to the origin tab — a
                      // trap that also loses the "start at overview" mental
                      // model users expect from a tab bar. Intra-tab
                      // navigation state (no `returnTab`) is preserved.
                      const nestedState = route.state;
                      const activeNested =
                        nestedState?.routes[nestedState.index ?? 0];
                      const activeParams = activeNested?.params;
                      const hasReturnTab =
                        activeParams != null &&
                        typeof activeParams === "object" &&
                        "returnTab" in activeParams &&
                        activeParams.returnTab != null;
                      if (nestedState?.key && hasReturnTab) {
                        navigation.dispatch({
                          ...StackActions.popToTop(),
                          target: nestedState.key,
                        });
                      }
                      navigation.navigate(route.name);
                    }
                  }
                }}
                onLongPress={() => {
                  navigation.emit({
                    type: "tabLongPress",
                    target: route.key,
                  });
                }}
              />
            );
          })}
        </View>
        <DocumentProcessingActivityControl
          processingItems={processingItems}
          updateProcessingItem={updateProcessingItem}
          dismissProcessingItem={dismissProcessingItem}
          onAddMoreDocuments={pickAndOpenDocumentUpload}
          onReviewDocumentValues={(patientDocumentId) =>
            navigation.navigate("Care", {
              screen: "Documents",
              params: patientDocumentId
                ? { initialDocumentId: patientDocumentId }
                : {},
            })
          }
        />
        {state.routes.map((route, index) => {
          if (route.name !== "Chat") return null;
          const { options } = descriptors[route.key]!;
          const isFocused = state.index === index;

          return (
            <TabButton
              key={route.key}
              isFocused={isFocused}
              accessibilityLabel={
                options.tabBarAccessibilityLabel ?? "Chat tab"
              }
              action="tab_chat"
              floating
              onPress={() => {
                haptic("light");
                const event = navigation.emit({
                  type: "tabPress",
                  target: route.key,
                  canPreventDefault: true,
                });
                if (!event.defaultPrevented) {
                  onOpenChat();
                }
              }}
              onLongPress={() => {
                navigation.emit({
                  type: "tabLongPress",
                  target: route.key,
                });
              }}
            >
              <ChatOrbIcon />
            </TabButton>
          );
        })}
      </View>
    </View>
  );
}

function ChatOrbIcon() {
  return (
    <View style={styles.chatOrb}>
      <GradientOrb size={34} />
    </View>
  );
}

function TabBarTransition({
  override,
  onOpenChat,
  ...props
}: BottomTabBarProps & {
  override: ReactNode | null;
  onOpenChat: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const [displayedOverride, setDisplayedOverride] = useState<ReactNode | null>(
    override,
  );
  const progress = useSharedValue(override ? 1 : 0);

  useEffect(() => {
    if (reducedMotion) return;

    if (override) {
      setDisplayedOverride(override);
    }

    progress.value = withTiming(
      override ? 1 : 0,
      {
        duration: 360,
        easing: Easing.out(Easing.cubic),
      },
      (finished) => {
        if (finished && !override) {
          runOnJS(setDisplayedOverride)(null);
        }
      },
    );
  }, [override, progress, reducedMotion]);

  const normalStyle = useAnimatedStyle(() => ({
    opacity: 1 - progress.value,
    transform: [
      { scale: interpolate(progress.value, [0, 1], [1, 0.94]) },
      { translateY: interpolate(progress.value, [0, 1], [0, 10]) },
    ],
  }));
  const overrideStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [
      { scale: interpolate(progress.value, [0, 1], [0.94, 1]) },
      { translateY: interpolate(progress.value, [0, 1], [10, 0]) },
    ],
  }));

  // The cross-fade is the whole point of these two layers, so without it there
  // is one bar to draw and no reason to keep either layer on the GPU: the
  // settled transforms the wrappers would go on writing keep the bar composited
  // for as long as it is on screen, and its rounded cap re-rasterizes.
  if (reducedMotion) {
    return (
      <View pointerEvents="box-none" style={styles.tabBarTransitionRoot}>
        <View pointerEvents="box-none" style={styles.tabBarTransitionLayer}>
          {override ?? <FloatingTabBar {...props} onOpenChat={onOpenChat} />}
        </View>
      </View>
    );
  }

  return (
    <View pointerEvents="box-none" style={styles.tabBarTransitionRoot}>
      <Animated.View
        pointerEvents={override ? "none" : "box-none"}
        style={[styles.tabBarTransitionLayer, normalStyle]}
      >
        <FloatingTabBar {...props} onOpenChat={onOpenChat} />
      </Animated.View>
      {displayedOverride ? (
        <Animated.View
          pointerEvents={override ? "box-none" : "none"}
          style={[styles.tabBarTransitionLayer, overrideStyle]}
        >
          {displayedOverride}
        </Animated.View>
      ) : null}
    </View>
  );
}

// Each tab is a module-level component: an inline render function is a new
// function on every navigator render, which re-renders the whole tab whenever
// the tab bar changes (e.g. the calendar's logging dock replacing it).
function HomeTab() {
  return (
    <ErrorBoundary>
      <View style={{ flex: 1 }}>
        <PlanHomeScreen />
        <OAuthPhoneLinkPrompt />
      </View>
    </ErrorBoundary>
  );
}

function CareTab() {
  return (
    <ErrorBoundary>
      <CareNavigator />
    </ErrorBoundary>
  );
}

function CostsTab() {
  return (
    <ErrorBoundary>
      <CostsNavigator />
    </ErrorBoundary>
  );
}

function AccessTab() {
  return (
    <ErrorBoundary>
      <AccessNavigator />
    </ErrorBoundary>
  );
}

function LearnTab() {
  return (
    <ErrorBoundary>
      <ChatNavigator initialRouteName="Learn" />
    </ErrorBoundary>
  );
}

function ChatTab() {
  return (
    <ErrorBoundary>
      <ChatNavigator initialRouteName="Assistant" />
    </ErrorBoundary>
  );
}

export default function MainTabNavigator() {
  const { showAuthGate } = useAuthGate();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const [tabBarOverride, setTabBarOverrideState] = useState<ReactNode | null>(
    null,
  );
  const [chatOpen, setChatOpen] = useState(false);
  const chatPanelWidth = Math.min(Math.max(width - 24, 296), 540);
  const chatPanelHeight = Math.min(
    Math.max(height - insets.top - insets.bottom - 48, 520),
    760,
  );
  const capturePress = usePressCapture("MainTabNavigator");
  const setTabBarOverride = useCallback((node: ReactNode | null) => {
    setTabBarOverrideState(node);
  }, []);
  const openChat = useCallback(() => setChatOpen(true), []);

  // The tab navigator remains available to support public/deep-linked
  // surfaces, but unauthenticated visitors should enter through the one
  // canonical login screen instead of a second auth form over the tabs.
  useEffect(() => {
    if (!showAuthGate) return;

    navigation.replace("Onboarding", { screen: "Login" });
  }, [navigation, showAuthGate]);

  // Don't paint the authenticated tab UI while the redirect above dispatches
  // (it runs in an effect, one frame after mount). Rendering the tabs first
  // makes a logged-out deep link to /home flash Home before bouncing to Login.
  // A plain background matches Home's own surface, so the frame is blank, not
  // a flash of authenticated content.
  if (showAuthGate) {
    return <View style={{ flex: 1, backgroundColor: EMBER.colors.bg }} />;
  }

  return (
    <View style={{ flex: 1 }}>
      <DocumentUploadControllerProvider>
        <TabBarOverrideProvider
          setOverride={setTabBarOverride}
          openChat={openChat}
        >
          <Tab.Navigator
            tabBar={(props) => (
              <>
                <TabBarTransition
                  {...props}
                  override={tabBarOverride}
                  onOpenChat={() => setChatOpen(true)}
                />
              </>
            )}
            screenOptions={{ headerShown: false }}
          >
            <Tab.Screen name="Home" component={HomeTab} />
            <Tab.Screen name="Care" component={CareTab} />
            <Tab.Screen name="Costs" component={CostsTab} />
            <Tab.Screen name="Access" component={AccessTab} />
            <Tab.Screen
              name="Learn"
              component={LearnTab}
              options={{ tabBarLabel: "Learn" }}
            />
            <Tab.Screen
              name="Chat"
              component={ChatTab}
              options={{ tabBarLabel: "Chat" }}
            />
          </Tab.Navigator>
        </TabBarOverrideProvider>
      </DocumentUploadControllerProvider>
      {chatOpen ? (
        <View style={styles.chatOverlay}>
          <Pressable
            accessible={false}
            onPress={() => {
              capturePress("dismiss_chat_backdrop");
              setChatOpen(false);
            }}
            style={styles.chatBackdrop}
          />
          <View
            style={[
              styles.chatPanel,
              { width: chatPanelWidth, height: chatPanelHeight },
            ]}
          >
            <ErrorBoundary>
              <ChatHubScreen onClose={() => setChatOpen(false)} />
            </ErrorBoundary>
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  tabBarTransitionRoot: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 120,
    zIndex: 200,
  },
  tabBarTransitionLayer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 120,
  },
  tabBarOuter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 200,
  },
  tabBarPill: {
    flexDirection: "row",
    backgroundColor: "rgba(20, 20, 20, 0.92)",
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 6,
    gap: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
  tabBarCluster: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  tabButton: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  floatingTabButton: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(20, 20, 20, 0.92)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
  customIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
  },
  chatOrb: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  chatOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 500,
    elevation: 500,
    alignItems: "center",
    justifyContent: "center",
  },
  chatBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(42, 37, 34, 0.28)",
    ...(Platform.OS === "web"
      ? ({ backdropFilter: "blur(5px)" } as Record<string, string>)
      : null),
  },
  chatPanel: {
    overflow: "hidden",
  },
  iconWrapActive: {},
});
